import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { ActividadViewModel } from "src/app/_core/models/centros/actividad.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

/**
 * Selector de Actividad. Carga el maestro y expone el id seleccionado como
 * ControlValueAccessor; la presentación (input + desplegable `.ds`, filtrado,
 * teclado) la delega en `app-ds-autocomplete`.
 */
@Component({
	selector: "app-actividad-autocomplete",
	templateUrl: "./actividad-autocomplete.component.html",
	styleUrls: ["./actividad-autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ActividadAutocompleteComponent),
			multi: true,
		},
	],
	standalone: false,
})
export class ActividadAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Actividad";
	@Input() placeholder = "Buscar actividad por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción de la actividad";

	// Outputs
	@Output() actividadSelected = new EventEmitter<ActividadViewModel>();

	// State
	actividades: ActividadViewModel[] = [];
	actividadId: number | null = null;
	selectedActividad: ActividadViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadActividades();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.actividadId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	private loadActividades(): void {
		this.masterService
			.getActividades()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (actividades) => {
					this.actividades = actividades;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar las actividades",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedActividad =
			this.actividadId != null
				? (this.actividades.find((a) => a.id === this.actividadId) ??
					null)
				: null;
	}

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(actividad: ActividadViewModel | null): void {
		this.actividadId = actividad ? actividad.id : null;
		this.selectedActividad = actividad;
		this.onChange(this.actividadId);
		this.onTouched();
		if (actividad) {
			this.actividadSelected.emit(actividad);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}

	/** Texto mostrado para una actividad. */
	displayActividad = (actividad: ActividadViewModel): string =>
		actividad && actividad.codigo && actividad.descripcion
			? `${actividad.codigo} - ${actividad.descripcion}`
			: "";
}
